import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * The state and the actions every one of the five collections needs, built
 * once.
 *
 * Five nouns get five stores from this the same way five nouns get their routes
 * from one factory on each side of the wire. What differs between them is which
 * relationships they hold, and each noun's store adds those beside these.
 *
 * **Every action resolves, and none of them throws.** A failure lands in `error`
 * as the caught `AdministrationApiError` and the action answers `false`. A view
 * then reads `error.code` to say what happened and `error.diagnostics` to
 * underline a bad query, without knowing that `fetch` exists. The alternative —
 * every caller wrapping every action in a `try` — is one missing `try` away from
 * an unhandled rejection in a click handler.
 *
 * @param {Object} config
 * @param {string} config.id       The Pinia store id.
 * @param {Object} config.client   The noun's methods off the transport.
 * @param {Function} [config.extend] Called inside the setup with the shared
 *   bindings, and whatever it returns joins the store. This is the seam for the
 *   relationships one noun holds — a Company's `referencing`, a Dealership's
 *   claims — and it runs inside `defineStore` so the state it creates is that
 *   store's, not a module's.
 */
export function defineCollectionStore({ id, client, extend }) {
    return defineStore(id, function () {
        // The page currently held, and what asked for it.
        const documents = ref([]);
        const totalCount = ref(0);
        const page = ref(1);
        const pageSize = ref(null);
        const status = ref(null);
        const query = ref('');
        // The order a search asks for, as `{ field, direction }`, or null for
        // the server's own. Held with the query, because page two of a sorted
        // search is that sort again.
        const sort = ref(null);

        // The one document a detail screen is looking at.
        const current = ref(null);

        // What the collection says is searchable. Read once and held: it is a
        // constant on the server, built at module load.
        const surface = ref(null);

        const loading = ref(false);
        const error = ref(null);

        // Which read produced the page on screen. Held because `list` and
        // `search` are different questions — one filters by status and lets the
        // server default it, the other matches whatever an empty query matches
        // — so re-running the wrong one after a write silently changes what is
        // shown.
        const lastRead = ref(null);

        const pageCount = computed(function () {
            if (pageSize.value === null || pageSize.value <= 0) {
                return null;
            }
            return Math.ceil(totalCount.value / pageSize.value);
        });

        /**
         * Run one call against the transport, and let its failure land in
         * `error` rather than in the caller.
         *
         * `loading` is set and cleared here so no action can forget to clear it
         * on the failing path, which is the path that matters.
         *
         * The clear is written on both paths rather than in a `finally`. A
         * `finally` would guarantee it runs even if the `catch` block itself
         * threw, and the `catch` block is one assignment that cannot — so it
         * buys nothing here and leaves behind an arm no test can reach, which
         * this package's coverage gate is right to refuse.
         */
        async function attempt(work) {
            loading.value = true;
            error.value = null;
            try {
                const value = await work();
                loading.value = false;
                return { ok: true, value };
            } catch (err) {
                error.value = err;
                loading.value = false;
                return { ok: false };
            }
        }

        function holdPage(result) {
            documents.value = result.documents;
            totalCount.value = result.meta.totalCount;
        }

        /**
         * A page of documents carrying `status`, sorted by name.
         *
         * `status` and `pageSize` are sent only once a caller has named one:
         * the store holds no opinion about either until told, and the server's
         * own defaults — `status: 'active'`, and entities-core's `pageSize: 50`
         * — are its to apply. A client repeating them would own a second copy.
         *
         * `page` is not like those two. Which page is on screen is this store's
         * own state, a paginator binds to it, and before anything is asked the
         * truthful answer is the first one. So it is always sent, and it says
         * where the store is rather than restating what the server would assume.
         */
        async function list(options = {}) {
            if (options.status !== undefined) {
                status.value = options.status;
            }
            if (options.page !== undefined) {
                page.value = options.page;
            }
            if (options.pageSize !== undefined) {
                pageSize.value = options.pageSize;
            }
            const attempted = await attempt(function () {
                return client.list({
                    status: status.value ?? undefined,
                    page: page.value,
                    pageSize: pageSize.value ?? undefined
                });
            });
            if (attempted.ok === false) {
                return false;
            }
            lastRead.value = 'list';
            holdPage(attempted.value);
            return true;
        }

        /**
         * Which search the held page is allowed to come from.
         *
         * Two searches can be in flight at once and the network decides which
         * answers first, so the newest request takes a ticket and any older
         * answer arriving after it is dropped rather than held.
         */
        let searchTicket = 0;

        /**
         * Search with FQL, holding the query that produced the page.
         *
         * The query is held rather than passed and forgotten because paging
         * through results has to re-send it: this server pages a search the same
         * way it pages a list, and page two of a query is that query again.
         */
        async function search(options = {}) {
            if (options.query !== undefined) {
                query.value = options.query;
            }
            if (options.page !== undefined) {
                page.value = options.page;
            }
            if (options.pageSize !== undefined) {
                pageSize.value = options.pageSize;
            }
            if (options.sort !== undefined) {
                sort.value = options.sort;
            }
            searchTicket = searchTicket + 1;
            const ticket = searchTicket;
            const attempted = await attempt(function () {
                return client.search(query.value, {
                    page: page.value,
                    pageSize: pageSize.value ?? undefined,
                    sort: sort.value?.field,
                    direction: sort.value?.direction
                });
            });
            if (attempted.ok === false) {
                return false;
            }
            // A later search has already been asked for, and its answer is the
            // one the page should show. Typing and then clicking page two sends
            // two searches, and the slower first one used to land last and put
            // page one back under a paginator reading two.
            if (ticket !== searchTicket) {
                return false;
            }
            lastRead.value = 'search';
            holdPage(attempted.value);
            return true;
        }

        /**
         * Run a call that answers one whole document, and hold what it answered
         * as `current`.
         *
         * Every write on this server answers the updated document, so a screen
         * that just saved is looking at what it saved without a second read. The
         * reads that address one document — `load`, and an Auction House's
         * `loadBySlug` — land the same way, which is why this is named for what
         * it holds rather than for writing.
         */
        async function holdCurrent(work) {
            const attempted = await attempt(work);
            if (attempted.ok === false) {
                return false;
            }
            current.value = attempted.value;
            return true;
        }

        /** Read one into `current`. */
        async function load(id_) {
            return await holdCurrent(function () {
                return client.read(id_);
            });
        }

        async function create(body) {
            return await holdCurrent(function () {
                return client.create(body);
            });
        }

        /**
         * Edit one.
         *
         * The body goes through as given: on this server `null` clears an
         * optional property and an absent key leaves it alone, so nothing here
         * fills a missing key in.
         */
        async function patch(id_, body) {
            return await holdCurrent(function () {
                return client.patch(id_, body);
            });
        }

        async function setStatus(id_, newStatus) {
            return await holdCurrent(function () {
                return client.setStatus(id_, newStatus);
            });
        }

        async function restore(id_) {
            return await holdCurrent(function () {
                return client.restore(id_);
            });
        }

        /**
         * Read the current page again, the way it was read the first time.
         *
         * Every write route on this server answers the updated document and
         * touches nothing else, so a page held here is stale the moment one
         * lands. Callers were doing this themselves and had to remember which
         * read produced the page — re-running `search` over a page that came
         * from `list` quietly drops the status filter, and nothing says so.
         *
         * Deliberately NOT automatic after a write. A store that refetched on
         * every write would turn one action into two requests even when the
         * caller is about to make three more edits or navigate away, and
         * several of these routes are used in exactly that run.
         *
         * Answers `false` when nothing has been read yet — there is no page to
         * refresh, and guessing one would invent a query nobody asked.
         */
        async function refresh() {
            if (lastRead.value === null) {
                return false;
            }
            if (lastRead.value === 'list') {
                return await list();
            }
            return await search();
        }

        /** The values a field offers, for a dropdown. An empty list is an answer. */
        async function suggest(field, prefix, limit) {
            const attempted = await attempt(function () {
                return client.suggest(field, prefix, limit);
            });
            if (attempted.ok === false) {
                return [];
            }
            return attempted.value.values;
        }

        /**
         * Read what is searchable, once.
         *
         * A second call answers the held copy rather than the server: the
         * declaration is a module-load constant over there, so re-reading it
         * costs a request and can never change the answer.
         */
        async function loadSurface() {
            if (surface.value !== null) {
                return true;
            }
            const attempted = await attempt(function () {
                return client.searchableSurface();
            });
            if (attempted.ok === false) {
                return false;
            }
            surface.value = attempted.value.fields;
            return true;
        }

        const shared = {
            documents,
            totalCount,
            page,
            pageSize,
            status,
            query,
            sort,
            current,
            surface,
            loading,
            error,
            pageCount,
            attempt,
            holdCurrent,
            lastRead,
            list,
            search,
            refresh,
            load,
            create,
            patch,
            setStatus,
            restore,
            suggest,
            loadSurface
        };

        if (extend === undefined) {
            return shared;
        }
        // `attempt` and `holdCurrent` are handed over so an added action fails the
        // same way every shared one does — into `error`, answering false, with
        // `loading` cleared. An extension reaching for the client directly and
        // throwing would be the one action a view has to wrap.
        return { ...shared, ...extend({ client, attempt, holdCurrent }) };
    });
}

export default defineCollectionStore;
